"use client";

import React from 'react';
// TODO: Migrate to Next.js App Router metadata API when stable
import Head from 'next/head';
import { usePathname } from 'next/navigation';
import siteConfig from '../../config/siteConfig';

// Define interface for language objects used in AlternateLanguageUrls
interface Language {
  locale: string; // e.g., 'en', 'es', 'fr'
  path: string;   // e.g., '/en', '/es', '/fr'
}

// Props for CanonicalUrl component
interface CanonicalUrlProps {
  customPath?: string; // Optional custom path to override current path
}

// Props for AlternateLanguageUrls component
interface AlternateLanguageUrlsProps {
  languages?: Language[]; // Optional array of language objects
}

// Props for PaginationLinks component
interface PaginationLinksProps {
  prevUrl?: string; // Optional URL for the previous page
  nextUrl?: string; // Optional URL for the next page
}

// Props for SEOLinks component
interface SEOLinksProps {
  customPath?: string; // Optional custom path for canonical URL
  languages?: Language[]; // Optional array of language objects
  prevUrl?: string; // Optional URL for the previous page
  nextUrl?: string; // Optional URL for the next page
}

// Utility to validate URLs
const isValidUrl = (urlString: string): boolean => {
  try {
    new URL(urlString);
    return true;
  } catch {
    return false;
  }
};

/**
 * Component to add canonical URL to any page
 * @param props - Props for canonical URL
 * @returns React component adding link tag to head
 */
const CanonicalUrl: React.FC<CanonicalUrlProps> = ({ customPath = '' }) => {
  const pathname = usePathname();
  const constructedUrl = `${siteConfig.baseUrl}${customPath || pathname}`.replace(/\/+$/, '') + '/';
  const canonicalUrl = isValidUrl(constructedUrl) ? constructedUrl : siteConfig.baseUrl;

  return (
    <Head>
      <link rel="canonical" href={canonicalUrl} />
    </Head>
  );
};

/**
 * Component to add alternate language URLs for internationalization
 * @param props - Props for alternate language URLs
 * @returns React component adding link tags to head
 */
const AlternateLanguageUrls: React.FC<AlternateLanguageUrlsProps> = ({ languages = [] }) => {
  const defaultUrl = `${siteConfig.baseUrl}`.replace(/\/+$/, '') + '/';
  const isDefaultUrlValid = isValidUrl(defaultUrl);

  return (
    <Head>
      {languages.map((lang) => {
        const constructedUrl = `${siteConfig.baseUrl}${lang.path}`.replace(/\/+$/, '') + '/';
        return isValidUrl(constructedUrl) ? (
          <link key={lang.locale} rel="alternate" hrefLang={lang.locale} href={constructedUrl} />
        ) : null;
      })}
      {isDefaultUrlValid && (
        <link rel="alternate" hrefLang="x-default" href={defaultUrl} />
      )}
    </Head>
  );
};

/**
 * Component to add prev/next links for paginated content
 * @param props - Props for pagination links
 * @returns React component adding link tags to head
 */
const PaginationLinks: React.FC<PaginationLinksProps> = ({ prevUrl = '', nextUrl = '' }) => {
  const constructedPrevUrl = prevUrl ? `${siteConfig.baseUrl}${prevUrl}`.replace(/\/+$/, '') + '/' : '';
  const constructedNextUrl = nextUrl ? `${siteConfig.baseUrl}${nextUrl}`.replace(/\/+$/, '') + '/' : '';

  return (
    <Head>
      {prevUrl && isValidUrl(constructedPrevUrl) && (
        <link rel="prev" href={constructedPrevUrl} />
      )}
      {nextUrl && isValidUrl(constructedNextUrl) && (
        <link rel="next" href={constructedNextUrl} />
      )}
    </Head>
  );
};

/**
 * Component to add all SEO link tags (canonical, alternate, pagination)
 * @param props - Properties for all link types
 * @returns React component with all SEO link tags
 */
const SEOLinks: React.FC<SEOLinksProps> = ({ 
  customPath = '', 
  languages = [], 
  prevUrl = '', 
  nextUrl = '' 
}) => {
  return (
    <>
      <CanonicalUrl customPath={customPath} />
      {languages.length > 0 && <AlternateLanguageUrls languages={languages} />}
      {(prevUrl || nextUrl) && <PaginationLinks prevUrl={prevUrl} nextUrl={nextUrl} />}
    </>
  );
}

export default SEOLinks;